import type { FunctionComponent, ReactElement } from 'react';
import { useIsFeatureEnabled } from '../../../helpers/features';
import type { FlagName } from '../../../../shared/featureFlags/types';

type Props = {
  flagName: FlagName;
  isNotEnabled?: boolean;
};

/**
 * Access gate which renders any children, but only if the user has the specified FeatureFlag.
 *
 * @example - Button only renders if My Wonderful Feature Flag is enabled:
 * <FeatureFlagRequired flagName={MY_WONDERFUL_FEATURE_FLAG}>
 *   <Button>See Issues</Button>
 * </FeatureFlagRequired>
 * <FeatureFlagRequired isNotEnabled flagName={MY_WONDERFUL_FEATURE_FLAG}>
 *   <Button>See Old Issues</Button>
 * </FeatureFlagRequired>
 */
const FeatureFlagRequired: FunctionComponent<Props> = ({ children, flagName, isNotEnabled }) => {
  const isEnabled = useIsFeatureEnabled(flagName);
  if (isNotEnabled) {
    return !isEnabled ? (children as ReactElement) : null; // Required to avoid errors in the consuming component.
  }
  return isEnabled ? (children as ReactElement) : null; // Required to avoid errors in the consuming component.
};

export default FeatureFlagRequired;
